/**
 * Modal para crear un cobro (cargo)
 */

import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button, Input, Select } from "@/components/ui";
import { DatePickerField } from "@/components/ui/DatePicker";
import { X, Loader2, Receipt, Search, User } from "lucide-react";
import { useCreateCharge } from "@/hooks/useFinance";
import { useMembersByClub } from "@/hooks/usePersons";
import { useFinancialConceptsByType } from "@/hooks/useCatalogs";
import { useClub } from "@/hooks/useOrganization";
import { usePermissions } from "@/hooks/usePermissions";
import type { MemberWithPerson as Member, Charge } from "@/types";
import type { FinancialConcept } from "@/lib/services/catalogs";

const chargeSchema = z.object({
  memberId: z.string().min(1, "Selecciona un miembro"),
  personId: z.string().min(1, "Persona requerida"),
  conceptType: z.string().min(1, "Selecciona un concepto"),
  description: z.string().optional(),
  amount: z.number().min(1, "El monto debe ser mayor a 0"),
  dueDate: z.string().optional(),
});

type ChargeForm = z.infer<typeof chargeSchema>;

interface ChargeModalProps {
  isOpen: boolean;
  onClose: () => void;
  clubId: string;
  periodId: string;
  charge?: Charge | null;
}

const conceptLabels: Record<string, string> = {
  anualidad: "Anualidad",
  mensualidad: "Cuota Mensual",
  evento: "Evento",
  uniforme: "Uniforme",
  material: "Material",
  otro: "Otro",
};

export default function ChargeModal({
  isOpen,
  onClose,
  clubId,
  periodId,
  charge,
}: ChargeModalProps) {
  const isEditing = !!charge;

  const createMutation = useCreateCharge();
  const { data: members, isLoading: loadingMembers } = useMembersByClub(clubId);
  const { data: incomeConcepts } = useFinancialConceptsByType("income");
  const { data: club } = useClub(clubId);
  const { canSeeMember } = usePermissions();

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    control,
    formState: { errors, isSubmitting },
  } = useForm<ChargeForm>({
    resolver: zodResolver(chargeSchema),
    defaultValues: {
      amount: 0,
    },
  });

  // Filtrar miembros activos y por sección del director (si aplica)
  const activeMembers =
    members?.filter(
      (m) =>
        m.status === "active" &&
        canSeeMember({
          clubId: m.clubId,
          section: m.section,
          staffSection: m.staffSection,
        }),
    ) || [];
  const filteredMembers = activeMembers.filter((m) => {
    const memberName =
      m.person.fullName || `${m.person.firstName} ${m.person.lastName}`;
    return memberName.toLowerCase().includes(searchTerm.toLowerCase());
  });

  // Cuando se selecciona un concepto, establecer monto por defecto (si aplica)
  // useEffect para auto-llenar monto se puede agregar si los conceptos tienen defaultAmount

  const handleSelectMember = (member: Member) => {
    setSelectedMember(member);
    setValue("memberId", member.id);
    setValue("personId", member.personId || member.id);
    setSearchTerm("");
  };

  const onSubmit = async (data: ChargeForm) => {
    if (!selectedMember) return;

    const selectedConcept: FinancialConcept | undefined = incomeConcepts?.find(
      (c: FinancialConcept) => c.id === data.conceptType,
    );

    const chargeData = {
      clubId,
      periodId,
      memberId: data.memberId,
      personId: data.personId,
      personName: selectedMember.person.fullName,
      conceptType: data.conceptType,
      conceptName:
        selectedConcept?.name ||
        conceptLabels[data.conceptType] ||
        data.conceptType,
      description: data.description || "",
      amount: data.amount,
      currency: "COP",
      dueDate: data.dueDate ? new Date(data.dueDate) : undefined,
      status: "pending" as const,
      amountPaid: 0,
      balance: data.amount,
    };

    try {
      await createMutation.mutateAsync(chargeData as any);
      reset();
      setSelectedMember(null);
      onClose();
    } catch (error) {
      console.error("Error creating charge:", error);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl animate-in fade-in zoom-in-95 duration-200">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl flex items-center justify-center shadow-md shadow-blue-500/25">
              <Receipt className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {isEditing ? "Editar Cobro" : "Nuevo Cobro"}
              </h2>
              <p className="text-sm text-gray-500">{club?.name}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-xl transition-colors duration-200"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
          {/* Buscar miembro */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Miembro *
            </label>

            {selectedMember ? (
              <div className="flex items-center justify-between p-3 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl flex items-center justify-center shadow-md shadow-blue-500/25">
                    <User className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-blue-900">
                      {selectedMember.person.fullName}
                    </p>
                    <p className="text-sm text-blue-600">
                      {selectedMember.position || "Miembro"}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedMember(null)}
                  className="text-blue-600 hover:text-blue-800 transition-colors duration-200"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="relative">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Buscar miembro por nombre..."
                    className="w-full pl-10 pr-4 py-2.5 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-300"
                  />
                </div>

                {searchTerm && (
                  <div className="absolute z-10 w-full mt-1 bg-white border-2 border-gray-100 rounded-xl shadow-lg max-h-48 overflow-y-auto">
                    {loadingMembers ? (
                      <div className="p-4 text-center text-gray-500">
                        <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                      </div>
                    ) : filteredMembers.length === 0 ? (
                      <div className="p-4 text-center text-gray-500">
                        No se encontraron miembros
                      </div>
                    ) : (
                      filteredMembers.slice(0, 10).map((member) => (
                        <button
                          key={member.id}
                          type="button"
                          onClick={() => handleSelectMember(member)}
                          className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 transition-colors duration-150"
                        >
                          <User className="w-4 h-4 text-gray-400" />
                          <div>
                            <p className="font-medium text-gray-900">
                              {member.person.fullName}
                            </p>
                            <p className="text-sm text-gray-500">
                              {member.position || "Miembro"}
                            </p>
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}
            {errors.memberId && (
              <p className="text-sm text-red-500 mt-1">
                {errors.memberId.message}
              </p>
            )}
          </div>

          {/* Concepto */}
          <Select
            label="Concepto *"
            {...register("conceptType")}
            error={errors.conceptType?.message}
          >
            <option value="">Seleccionar concepto</option>
            {incomeConcepts
              ?.filter((c: FinancialConcept) => c.status === "active")
              .map((concept: FinancialConcept) => (
                <option key={concept.id} value={concept.id}>
                  {concept.name}
                </option>
              ))}
            <option value="anualidad">Anualidad</option>
            <option value="mensualidad">Cuota Mensual</option>
            <option value="evento">Evento</option>
            <option value="uniforme">Uniforme</option>
            <option value="material">Material</option>
            <option value="otro">Otro</option>
          </Select>

          {/* Descripción */}
          <Input
            label="Descripción"
            {...register("description")}
            error={errors.description?.message}
            placeholder="Detalle del cobro (opcional)"
          />

          {/* Monto */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Monto (COP) *
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                $
              </span>
              <input
                type="number"
                {...register("amount", { valueAsNumber: true })}
                className="w-full pl-8 pr-4 py-2.5 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-300"
                placeholder="0"
              />
            </div>
            {errors.amount && (
              <p className="text-sm text-red-500 mt-1">
                {errors.amount.message}
              </p>
            )}
          </div>

          {/* Fecha límite */}
          <DatePickerField
            control={control}
            name="dueDate"
            label="Fecha Límite de Pago"
            error={errors.dueDate?.message}
          />

          {/* Botones */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={
                isSubmitting || createMutation.isPending || !selectedMember
              }
            >
              {isSubmitting || createMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Guardando...
                </>
              ) : (
                <>
                  <Receipt className="w-4 h-4 mr-2" />
                  {isEditing ? "Guardar Cambios" : "Crear Cobro"}
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
